import {useMemoizedSelector} from '../../../../state/hooks';
import type * as PanelTypes from '../../../../state/views/panel/types';
import {
  makeSectionMediaActualSizeSelector,
  makeSectionMediaAlwaysOriginalSelector,
  makeSectionMediaBoundingBoxConfigSelector,
  makeSectionMediaCompareSettingsSelector,
  makeSectionMediaDisableVideoStreamSelector,
  makeSectionMediaFitToDimensionSelector,
  makeSectionMediaMaxGalleryItemsSelector,
  makeSectionMediaMaxRunsSelector,
  makeSectionMediaMaxYAxisCountSelector,
  makeSectionMediaModeSelector,
  makeSectionMediaObject3DBackgroundColorSelector,
  makeSectionMediaPixelatedSelector,
  makeSectionMediaPointColorSelector,
  makeSectionMediaSegmentationMaskConfigSelector,
  makeSectionMediaSliderKeySelector,
  makeSectionMediaSnapToExistingStepSelector,
  makeSectionMediaStepStrideLengthSelector,
  makeSectionMediaUseRightHandedSystemSelector,
  makeSectionMediaVideoPlaybackSyncAutoplaySelector,
  makeSectionMediaVideoPlaybackSyncLoopSelector,
  makeSectionMediaVideoPlaybackSyncSelector,
  makeSectionMediaVideoPlayerTimeStepSelector,
} from '../../../../state/views/panelBankSectionConfig/selectors';
import {
  makeWorkspaceMediaActualSizeSelector,
  makeWorkspaceMediaAlwaysOriginalSelector,
  makeWorkspaceMediaBoundingBoxConfigSelector,
  makeWorkspaceMediaCompareSettingsSelector,
  makeWorkspaceMediaDisableVideoStreamSelector,
  makeWorkspaceMediaFitToDimensionSelector,
  makeWorkspaceMediaMaxGalleryItemsSelector,
  makeWorkspaceMediaMaxRunsSelector,
  makeWorkspaceMediaMaxYAxisCountSelector,
  makeWorkspaceMediaModeSelector,
  makeWorkspaceMediaObject3DBackgroundColorSelector,
  makeWorkspaceMediaPixelatedSelector,
  makeWorkspaceMediaPointColorSelector,
  makeWorkspaceMediaSegmentationMaskConfigSelector,
  makeWorkspaceMediaSliderKeySelector,
  makeWorkspaceMediaSnapToExistingStepSelector,
  makeWorkspaceMediaStepStrideLengthSelector,
  makeWorkspaceMediaUseRightHandedSystemSelector,
  makeWorkspaceMediaVideoPlaybackSyncSelector,
  makeWorkspaceMediaVideoPlayerTimeStepSelector,
  makeWorkspaceSettingsVideoPlaybackSyncAutoplaySelector,
  makeWorkspaceSettingsVideoPlaybackSyncLoopSelector,
} from '../../../../state/views/workspaceSettings/selectors';
import {type LayoutMode} from '../../../PanelMediaBrowser/common';
import {useMaybeWorkspaceRefsContext} from '../../../Workspace/WorkspaceRefsContext';
import {
  makeCascadingSectionMediaActualSizeSelector,
  makeCascadingSectionMediaAlwaysOriginalSelector,
  makeCascadingSectionMediaBoundingBoxConfigSelector,
  makeCascadingSectionMediaCompareSettingsSelector,
  makeCascadingSectionMediaDisableVideoStreamSelector,
  makeCascadingSectionMediaFitToDimensionSelector,
  makeCascadingSectionMediaMaxGalleryItemsSelector,
  makeCascadingSectionMediaMaxRunsSelector,
  makeCascadingSectionMediaMaxYAxisCountSelector,
  makeCascadingSectionMediaModeSelector,
  makeCascadingSectionMediaObject3DBackgroundColorSelector,
  makeCascadingSectionMediaPixelatedSelector,
  makeCascadingSectionMediaPointColorSelector,
  makeCascadingSectionMediaSegmentationMaskConfigSelector,
  makeCascadingSectionMediaSliderKeySelector,
  makeCascadingSectionMediaSnapToExistingStepSelector,
  makeCascadingSectionMediaStepStrideLengthSelector,
  makeCascadingSectionMediaUseRightHandedSystemSelector,
  makeCascadingSectionMediaVideoPlaybackSyncAutoplaySelector,
  makeCascadingSectionMediaVideoPlaybackSyncLoopSelector,
  makeCascadingSectionMediaVideoPlaybackSyncSelector,
  makeCascadingSectionMediaVideoPlayerTimeStepSelector,
  makeMediaActualSizeDisplaySelector,
  makeMediaAlwaysOriginalDisplaySelector,
  makeMediaBoundingBoxConfigDisplaySelector,
  makeMediaCompareSettingsDisplaySelector,
  makeMediaDisableVideoStreamDisplaySelector,
  makeMediaFitToDimensionDisplaySelector,
  makeMediaMaxGalleryItemsDisplaySelector,
  makeMediaMaxRunsDisplaySelector,
  makeMediaMaxYAxisCountDisplaySelector,
  makeMediaModeDisplaySelector,
  makeMediaObject3DBackgroundColorDisplaySelector,
  makeMediaPixelatedDisplaySelector,
  makeMediaPointColorDisplaySelector,
  makeMediaSegmentationMaskConfigDisplaySelector,
  makeMediaSliderKeyDisplaySelector,
  makeMediaSnapToExistingStepDisplaySelector,
  makeMediaStepStrideLengthDisplaySelector,
  makeMediaUseRightHandedSystemDisplaySelector,
  makeMediaVideoPlaybackSyncAutoplayDisplaySelector,
  makeMediaVideoPlaybackSyncDisplaySelector,
  makeMediaVideoPlaybackSyncIsInheritedSelector,
  makeMediaVideoPlaybackSyncLoopDisplaySelector,
  makeMediaVideoPlayerTimeStepDisplaySelector,
  makePanelMediaActualSizeSelector,
  makePanelMediaAlwaysOriginalSelector,
  makePanelMediaBoundingBoxConfigSelector,
  makePanelMediaCompareSettingsSelector,
  makePanelMediaDisableVideoStreamSelector,
  makePanelMediaFitToDimensionSelector,
  makePanelMediaMaxGalleryItemsSelector,
  makePanelMediaMaxRunsSelector,
  makePanelMediaMaxYAxisCountSelector,
  makePanelMediaModeSelector,
  makePanelMediaObject3DBackgroundColorSelector,
  makePanelMediaPixelatedSelector,
  makePanelMediaPointColorSelector,
  makePanelMediaSegmentationMaskConfigSelector,
  makePanelMediaSliderKeySelector,
  makePanelMediaSnapToExistingStepSelector,
  makePanelMediaStepStrideLengthSelector,
  makePanelMediaUseRightHandedSystemSelector,
  makePanelMediaVideoPlaybackSyncAutoplaySelector,
  makePanelMediaVideoPlaybackSyncLoopSelector,
  makePanelMediaVideoPlaybackSyncSelector,
  makePanelMediaVideoPlayerTimeStepSelector,
} from '../media/mediaSettingsSelectors';
import {mediaSettings} from '../media/settingFactory/mediaSettingsTable';
import {type SettingsLevel} from '../media/types';
import {
  useSectionSettingsContext,
  useSectionSettingsContextMaybe,
} from '../SectionSettingsContextProvider';

// chartTitle hooks are generated by the settings factory (panel-only: it
// never cascades from workspace or section). See
// ../media/settingFactory/mediaSettingsTable.ts.
const chartTitle = mediaSettings.chartTitle.hooks;

/** Raw panel-level chartTitle from the panel's own config. */
export const usePanelMediaChartTitle = chartTitle.usePanel;
/**
 * Whether to show the reset-to-parent button for chartTitle at the current
 * settings level. chartTitle is panel-only, so this is only ever true at the
 * panel level.
 */
export const useShouldShowMediaChartTitleResetAtLevel =
  chartTitle.useShouldShowReset;

// mediaKeys hooks are generated by the settings factory (panel-only: it
// never cascades from workspace or section). See
// ../media/settingFactory/mediaSettingsTable.ts.
const mediaKeys = mediaSettings.mediaKeys.hooks;

/** Raw panel-level mediaKeys from the panel's own config. */
export const usePanelMediaKeys = mediaKeys.usePanel;
/**
 * Whether to show the reset-to-parent button for mediaKeys at the current
 * settings level. mediaKeys is panel-only, so this is only ever true at the
 * panel level.
 */
export const useShouldShowMediaKeysResetAtLevel = mediaKeys.useShouldShowReset;

// sliderSync hooks are generated by the settings factory; these named exports
// are thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const sliderSync = mediaSettings.sliderSync.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaSliderSync = sliderSync.useWorkspace;
export const useMaybeWorkspaceMediaSliderSync = sliderSync.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaSliderSync = sliderSync.useSection;
export const useMaybeSectionMediaSliderSync = sliderSync.useMaybeSection;
/** Workspace → section cascaded value for display and section-level behavior. */
export const useCascadingSectionMediaSliderSync =
  sliderSync.useCascadingSection;
export const useMaybeCascadingSectionMediaSliderSync =
  sliderSync.useMaybeCascadingSection;

/**
 * Effective sliderSync for the media settings drawer at workspace or section level.
 * Use this instead of reading `config.sliderSync` for migrated section fields.
 */
export const useMediaSliderSyncDisplay = sliderSync.useDisplay;

/**
 * Whether to show the reset-to-parent button for sliderSync at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaSliderSyncResetAtLevel =
  sliderSync.useShouldShowReset;

// mediaSliderKey hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const mediaSliderKeyHooks = mediaSettings.mediaSliderKey.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaSliderKey = mediaSliderKeyHooks.useWorkspace;
export const useMaybeWorkspaceMediaSliderKey =
  mediaSliderKeyHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaSliderKey = mediaSliderKeyHooks.useSection;
export const useMaybeSectionMediaSliderKey =
  mediaSliderKeyHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaSliderKey =
  mediaSliderKeyHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaSliderKey =
  mediaSliderKeyHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaSliderKey = mediaSliderKeyHooks.usePanel;
/** Effective mediaSliderKey for the media settings drawer at the given settings level. */
export const useMediaSliderKeyDisplay = mediaSliderKeyHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for mediaSliderKey at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaSliderKeyResetAtLevel =
  mediaSliderKeyHooks.useShouldShowReset;

// mode hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const modeHooks = mediaSettings.mode.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaMode = modeHooks.useWorkspace;
export const useMaybeWorkspaceMediaMode = modeHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaMode = modeHooks.useSection;
export const useMaybeSectionMediaMode = modeHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaMode = modeHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaMode =
  modeHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaMode = modeHooks.usePanel;
/** Effective mode for the media settings drawer at the given settings level. */
export const useMediaModeDisplay = modeHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for mode at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaModeResetAtLevel = modeHooks.useShouldShowReset;

// snapToExistingStep hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const snapToExistingStepHooks = mediaSettings.snapToExistingStep.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaSnapToExistingStep =
  snapToExistingStepHooks.useWorkspace;
export const useMaybeWorkspaceMediaSnapToExistingStep =
  snapToExistingStepHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaSnapToExistingStep =
  snapToExistingStepHooks.useSection;
export const useMaybeSectionMediaSnapToExistingStep =
  snapToExistingStepHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaSnapToExistingStep =
  snapToExistingStepHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaSnapToExistingStep =
  snapToExistingStepHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaSnapToExistingStep = snapToExistingStepHooks.usePanel;
/** Effective snapToExistingStep for the media settings drawer at the given settings level. */
export const useMediaSnapToExistingStepDisplay =
  snapToExistingStepHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for snapToExistingStep at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaSnapToExistingStepResetAtLevel =
  snapToExistingStepHooks.useShouldShowReset;

// stepStrideLength hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const stepStrideLengthHooks = mediaSettings.stepStrideLength.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaStepStrideLength =
  stepStrideLengthHooks.useWorkspace;
export const useMaybeWorkspaceMediaStepStrideLength =
  stepStrideLengthHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaStepStrideLength = stepStrideLengthHooks.useSection;
export const useMaybeSectionMediaStepStrideLength =
  stepStrideLengthHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaStepStrideLength =
  stepStrideLengthHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaStepStrideLength =
  stepStrideLengthHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaStepStrideLength = stepStrideLengthHooks.usePanel;
/** Effective stepStrideLength for the media settings drawer at the given settings level. */
export const useMediaStepStrideLengthDisplay = stepStrideLengthHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for stepStrideLength at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaStepStrideLengthResetAtLevel =
  stepStrideLengthHooks.useShouldShowReset;

// pixelated hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const pixelatedHooks = mediaSettings.pixelated.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaPixelated = pixelatedHooks.useWorkspace;
export const useMaybeWorkspaceMediaPixelated = pixelatedHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaPixelated = pixelatedHooks.useSection;
export const useMaybeSectionMediaPixelated = pixelatedHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaPixelated =
  pixelatedHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaPixelated =
  pixelatedHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaPixelated = pixelatedHooks.usePanel;
/** Effective pixelated for the media settings drawer at the given settings level. */
export const useMediaPixelatedDisplay = pixelatedHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for pixelated at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaPixelatedResetAtLevel =
  pixelatedHooks.useShouldShowReset;

// columnCount hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const columnCountHooks = mediaSettings.columnCount.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaColumnCount = columnCountHooks.useWorkspace;
export const useMaybeWorkspaceMediaColumnCount =
  columnCountHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaColumnCount = columnCountHooks.useSection;
export const useMaybeSectionMediaColumnCount = columnCountHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaColumnCount =
  columnCountHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaColumnCount =
  columnCountHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaColumnCount = columnCountHooks.usePanel;

/**
 * Effective columnCount for the media settings drawer at the given settings level.
 * Use this instead of reading `config.columnCount` for migrated fields.
 */
export const useMediaColumnCountDisplay = columnCountHooks.useDisplay;

/**
 * Whether to show the reset-to-parent button for columnCount at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaColumnCountResetAtLevel =
  columnCountHooks.useShouldShowReset;

// maxRuns hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const maxRunsHooks = mediaSettings.maxRuns.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaMaxRuns = maxRunsHooks.useWorkspace;
export const useMaybeWorkspaceMediaMaxRuns = maxRunsHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaMaxRuns = maxRunsHooks.useSection;
export const useMaybeSectionMediaMaxRuns = maxRunsHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaMaxRuns = maxRunsHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaMaxRuns =
  maxRunsHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaMaxRuns = maxRunsHooks.usePanel;
/** Effective maxRuns for the media settings drawer at the given settings level. */
export const useMediaMaxRunsDisplay = maxRunsHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for maxRuns at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaMaxRunsResetAtLevel =
  maxRunsHooks.useShouldShowReset;

// maxGalleryItems hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const maxGalleryItemsHooks = mediaSettings.maxGalleryItems.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaMaxGalleryItems =
  maxGalleryItemsHooks.useWorkspace;
export const useMaybeWorkspaceMediaMaxGalleryItems =
  maxGalleryItemsHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaMaxGalleryItems = maxGalleryItemsHooks.useSection;
export const useMaybeSectionMediaMaxGalleryItems =
  maxGalleryItemsHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaMaxGalleryItems =
  maxGalleryItemsHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaMaxGalleryItems =
  maxGalleryItemsHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaMaxGalleryItems = maxGalleryItemsHooks.usePanel;
/** Effective maxGalleryItems for the media settings drawer at the given settings level. */
export const useMediaMaxGalleryItemsDisplay = maxGalleryItemsHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for maxGalleryItems at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaMaxGalleryItemsResetAtLevel =
  maxGalleryItemsHooks.useShouldShowReset;

// maxYAxisCount hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const maxYAxisCountHooks = mediaSettings.maxYAxisCount.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaMaxYAxisCount = maxYAxisCountHooks.useWorkspace;
export const useMaybeWorkspaceMediaMaxYAxisCount =
  maxYAxisCountHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaMaxYAxisCount = maxYAxisCountHooks.useSection;
export const useMaybeSectionMediaMaxYAxisCount =
  maxYAxisCountHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaMaxYAxisCount =
  maxYAxisCountHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaMaxYAxisCount =
  maxYAxisCountHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaMaxYAxisCount = maxYAxisCountHooks.usePanel;
/** Effective maxYAxisCount for the media settings drawer at the given settings level. */
export const useMediaMaxYAxisCountDisplay = maxYAxisCountHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for maxYAxisCount at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaMaxYAxisCountResetAtLevel =
  maxYAxisCountHooks.useShouldShowReset;

// actualSize hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const actualSizeHooks = mediaSettings.actualSize.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaActualSize = actualSizeHooks.useWorkspace;
export const useMaybeWorkspaceMediaActualSize =
  actualSizeHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaActualSize = actualSizeHooks.useSection;
export const useMaybeSectionMediaActualSize = actualSizeHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaActualSize =
  actualSizeHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaActualSize =
  actualSizeHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaActualSize = actualSizeHooks.usePanel;
/** Effective actualSize for the media settings drawer at the given settings level. */
export const useMediaActualSizeDisplay = actualSizeHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for actualSize at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaActualSizeResetAtLevel =
  actualSizeHooks.useShouldShowReset;

// fitToDimension hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const fitToDimensionHooks = mediaSettings.fitToDimension.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaFitToDimension = fitToDimensionHooks.useWorkspace;
export const useMaybeWorkspaceMediaFitToDimension =
  fitToDimensionHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaFitToDimension = fitToDimensionHooks.useSection;
export const useMaybeSectionMediaFitToDimension =
  fitToDimensionHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaFitToDimension =
  fitToDimensionHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaFitToDimension =
  fitToDimensionHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaFitToDimension = fitToDimensionHooks.usePanel;
/** Effective fitToDimension for the media settings drawer at the given settings level. */
export const useMediaFitToDimensionDisplay = fitToDimensionHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for fitToDimension at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaFitToDimensionResetAtLevel =
  fitToDimensionHooks.useShouldShowReset;

// videoActualSize hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const videoActualSizeHooks = mediaSettings.videoActualSize.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaVideoActualSize =
  videoActualSizeHooks.useWorkspace;
export const useMaybeWorkspaceMediaVideoActualSize =
  videoActualSizeHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaVideoActualSize = videoActualSizeHooks.useSection;
export const useMaybeSectionMediaVideoActualSize =
  videoActualSizeHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaVideoActualSize =
  videoActualSizeHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaVideoActualSize =
  videoActualSizeHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaVideoActualSize = videoActualSizeHooks.usePanel;
/** Effective videoActualSize for the media settings drawer at the given settings level. */
export const useMediaVideoActualSizeDisplay = videoActualSizeHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for videoActualSize at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaVideoActualSizeResetAtLevel =
  videoActualSizeHooks.useShouldShowReset;

// gallerySettings hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const gallerySettingsHooks = mediaSettings.gallerySettings.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaGallerySettings =
  gallerySettingsHooks.useWorkspace;
export const useMaybeWorkspaceMediaGallerySettings =
  gallerySettingsHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaGallerySettings = gallerySettingsHooks.useSection;
export const useMaybeSectionMediaGallerySettings =
  gallerySettingsHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaGallerySettings =
  gallerySettingsHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaGallerySettings =
  gallerySettingsHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaGallerySettings = gallerySettingsHooks.usePanel;
/**
 * Effective gallerySettings for the media settings drawer at the given settings level.
 * Use this instead of reading `config.gallerySettings` for migrated fields.
 */
export const useMediaGallerySettingsDisplay = gallerySettingsHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for gallerySettings at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaGallerySettingsResetAtLevel =
  gallerySettingsHooks.useShouldShowReset;

// gridSettings hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const gridSettingsHooks = mediaSettings.gridSettings.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaGridSettings = gridSettingsHooks.useWorkspace;
export const useMaybeWorkspaceMediaGridSettings =
  gridSettingsHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaGridSettings = gridSettingsHooks.useSection;
export const useMaybeSectionMediaGridSettings =
  gridSettingsHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaGridSettings =
  gridSettingsHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaGridSettings =
  gridSettingsHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaGridSettings = gridSettingsHooks.usePanel;
/**
 * Effective gridSettings for the media settings drawer at the given settings level.
 * Use this instead of reading `config.gridSettings` for migrated fields.
 */
export const useMediaGridSettingsDisplay = gridSettingsHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for gridSettings at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaGridSettingsResetAtLevel =
  gridSettingsHooks.useShouldShowReset;

// videoPlaybackSync hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const videoPlaybackSyncHooks = mediaSettings.videoPlaybackSync.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaVideoPlaybackSync =
  videoPlaybackSyncHooks.useWorkspace;
export const useMaybeWorkspaceMediaVideoPlaybackSync =
  videoPlaybackSyncHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaVideoPlaybackSync =
  videoPlaybackSyncHooks.useSection;
export const useMaybeSectionMediaVideoPlaybackSync =
  videoPlaybackSyncHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaVideoPlaybackSync =
  videoPlaybackSyncHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaVideoPlaybackSync =
  videoPlaybackSyncHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaVideoPlaybackSync = videoPlaybackSyncHooks.usePanel;
/** Effective videoPlaybackSync for the media settings drawer at the given settings level. */
export const useMediaVideoPlaybackSyncDisplay =
  videoPlaybackSyncHooks.useDisplay;

/**
 * Whether the sync toggle is locked because a higher level enabled it.
 */
export function useMediaVideoPlaybackSyncIsInherited(
  settingsLevel: SettingsLevel
): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaVideoPlaybackSyncIsInheritedSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel
  );
}

/**
 * Whether to show the reset-to-parent button for videoPlaybackSync at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaVideoPlaybackSyncResetAtLevel =
  videoPlaybackSyncHooks.useShouldShowReset;

// videoPlaybackSyncAutoplay hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const videoPlaybackSyncAutoplayHooks =
  mediaSettings.videoPlaybackSyncAutoplay.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaVideoPlaybackSyncAutoplay =
  videoPlaybackSyncAutoplayHooks.useWorkspace;
export const useMaybeWorkspaceMediaVideoPlaybackSyncAutoplay =
  videoPlaybackSyncAutoplayHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaVideoPlaybackSyncAutoplay =
  videoPlaybackSyncAutoplayHooks.useSection;
export const useMaybeSectionMediaVideoPlaybackSyncAutoplay =
  videoPlaybackSyncAutoplayHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaVideoPlaybackSyncAutoplay =
  videoPlaybackSyncAutoplayHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaVideoPlaybackSyncAutoplay =
  videoPlaybackSyncAutoplayHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaVideoPlaybackSyncAutoplay =
  videoPlaybackSyncAutoplayHooks.usePanel;
/** Effective videoPlaybackSyncAutoplay for the media settings drawer at the given settings level. */
export const useMediaVideoPlaybackSyncAutoplayDisplay =
  videoPlaybackSyncAutoplayHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for videoPlaybackSyncAutoplay at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaVideoPlaybackSyncAutoplayResetAtLevel =
  videoPlaybackSyncAutoplayHooks.useShouldShowReset;

// videoPlaybackSyncLoop hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const videoPlaybackSyncLoopHooks = mediaSettings.videoPlaybackSyncLoop.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaVideoPlaybackSyncLoop =
  videoPlaybackSyncLoopHooks.useWorkspace;
export const useMaybeWorkspaceMediaVideoPlaybackSyncLoop =
  videoPlaybackSyncLoopHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaVideoPlaybackSyncLoop =
  videoPlaybackSyncLoopHooks.useSection;
export const useMaybeSectionMediaVideoPlaybackSyncLoop =
  videoPlaybackSyncLoopHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaVideoPlaybackSyncLoop =
  videoPlaybackSyncLoopHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaVideoPlaybackSyncLoop =
  videoPlaybackSyncLoopHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaVideoPlaybackSyncLoop =
  videoPlaybackSyncLoopHooks.usePanel;
/** Effective videoPlaybackSyncLoop for the media settings drawer at the given settings level. */
export const useMediaVideoPlaybackSyncLoopDisplay =
  videoPlaybackSyncLoopHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for videoPlaybackSyncLoop at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaVideoPlaybackSyncLoopResetAtLevel =
  videoPlaybackSyncLoopHooks.useShouldShowReset;

// videoPlayerTimeStep hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const videoPlayerTimeStepHooks = mediaSettings.videoPlayerTimeStep.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaVideoPlayerTimeStep =
  videoPlayerTimeStepHooks.useWorkspace;
export const useMaybeWorkspaceMediaVideoPlayerTimeStep =
  videoPlayerTimeStepHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaVideoPlayerTimeStep =
  videoPlayerTimeStepHooks.useSection;
export const useMaybeSectionMediaVideoPlayerTimeStep =
  videoPlayerTimeStepHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaVideoPlayerTimeStep =
  videoPlayerTimeStepHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaVideoPlayerTimeStep =
  videoPlayerTimeStepHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaVideoPlayerTimeStep =
  videoPlayerTimeStepHooks.usePanel;
/** Effective videoPlayerTimeStep for the media settings drawer at the given settings level. */
export const useMediaVideoPlayerTimeStepDisplay =
  videoPlayerTimeStepHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for videoPlayerTimeStep at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaVideoPlayerTimeStepResetAtLevel =
  videoPlayerTimeStepHooks.useShouldShowReset;

// compareSettings hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const compareSettingsHooks = mediaSettings.compareSettings.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaCompareSettings =
  compareSettingsHooks.useWorkspace;
export const useMaybeWorkspaceMediaCompareSettings =
  compareSettingsHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaCompareSettings = compareSettingsHooks.useSection;
export const useMaybeSectionMediaCompareSettings =
  compareSettingsHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaCompareSettings =
  compareSettingsHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaCompareSettings =
  compareSettingsHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaCompareSettings = compareSettingsHooks.usePanel;
/** Effective compareSettings for the media settings drawer at the given settings level. */
export const useMediaCompareSettingsDisplay = compareSettingsHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for compareSettings at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaCompareSettingsResetAtLevel =
  compareSettingsHooks.useShouldShowReset;

// boundingBoxConfig hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const boundingBoxConfigHooks = mediaSettings.boundingBoxConfig.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaBoundingBoxConfig =
  boundingBoxConfigHooks.useWorkspace;
export const useMaybeWorkspaceMediaBoundingBoxConfig =
  boundingBoxConfigHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaBoundingBoxConfig =
  boundingBoxConfigHooks.useSection;
export const useMaybeSectionMediaBoundingBoxConfig =
  boundingBoxConfigHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaBoundingBoxConfig =
  boundingBoxConfigHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaBoundingBoxConfig =
  boundingBoxConfigHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaBoundingBoxConfig = boundingBoxConfigHooks.usePanel;
/** Effective boundingBoxConfig for the media settings drawer at the given settings level. */
export const useMediaBoundingBoxConfigDisplay =
  boundingBoxConfigHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for boundingBoxConfig at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaBoundingBoxConfigResetAtLevel =
  boundingBoxConfigHooks.useShouldShowReset;

// segmentationMaskConfig hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const segmentationMaskConfigHooks = mediaSettings.segmentationMaskConfig.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaSegmentationMaskConfig =
  segmentationMaskConfigHooks.useWorkspace;
export const useMaybeWorkspaceMediaSegmentationMaskConfig =
  segmentationMaskConfigHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaSegmentationMaskConfig =
  segmentationMaskConfigHooks.useSection;
export const useMaybeSectionMediaSegmentationMaskConfig =
  segmentationMaskConfigHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaSegmentationMaskConfig =
  segmentationMaskConfigHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaSegmentationMaskConfig =
  segmentationMaskConfigHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaSegmentationMaskConfig =
  segmentationMaskConfigHooks.usePanel;
/** Effective segmentationMaskConfig for the media settings drawer at the given settings level. */
export const useMediaSegmentationMaskConfigDisplay =
  segmentationMaskConfigHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for segmentationMaskConfig at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaSegmentationMaskConfigResetAtLevel =
  segmentationMaskConfigHooks.useShouldShowReset;

// useRightHandedSystem hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const useRightHandedSystemHooks = mediaSettings.useRightHandedSystem.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaUseRightHandedSystem =
  useRightHandedSystemHooks.useWorkspace;
export const useMaybeWorkspaceMediaUseRightHandedSystem =
  useRightHandedSystemHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaUseRightHandedSystem =
  useRightHandedSystemHooks.useSection;
export const useMaybeSectionMediaUseRightHandedSystem =
  useRightHandedSystemHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaUseRightHandedSystem =
  useRightHandedSystemHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaUseRightHandedSystem =
  useRightHandedSystemHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaUseRightHandedSystem =
  useRightHandedSystemHooks.usePanel;
/** Effective useRightHandedSystem for the media settings drawer at the given settings level. */
export const useMediaUseRightHandedSystemDisplay =
  useRightHandedSystemHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for useRightHandedSystem at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaUseRightHandedSystemResetAtLevel =
  useRightHandedSystemHooks.useShouldShowReset;

// pointColor hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const pointColorHooks = mediaSettings.pointColor.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaPointColor = pointColorHooks.useWorkspace;
export const useMaybeWorkspaceMediaPointColor =
  pointColorHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaPointColor = pointColorHooks.useSection;
export const useMaybeSectionMediaPointColor = pointColorHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaPointColor =
  pointColorHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaPointColor =
  pointColorHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaPointColor = pointColorHooks.usePanel;
/** Effective pointColor for the media settings drawer at the given settings level. */
export const useMediaPointColorDisplay = pointColorHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for pointColor at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaPointColorResetAtLevel =
  pointColorHooks.useShouldShowReset;

// moleculeConfig hooks are generated by the settings factory. See
// ../media/settingFactory/mediaSettingsTable.ts.
const moleculeConfigHooks = mediaSettings.moleculeConfig.hooks;

/** Effective moleculeConfig for the media settings drawer at the given settings level. */
export const useMediaMoleculeConfigDisplay = moleculeConfigHooks.useDisplay;
/** Whether to show the reset-to-parent button for moleculeConfig at the current settings level. */
export const useShouldShowMediaMoleculeConfigResetAtLevel =
  moleculeConfigHooks.useShouldShowReset;

// object3DBackgroundColor hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const object3DBackgroundColorHooks =
  mediaSettings.object3DBackgroundColor.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaObject3DBackgroundColor =
  object3DBackgroundColorHooks.useWorkspace;
export const useMaybeWorkspaceMediaObject3DBackgroundColor =
  object3DBackgroundColorHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaObject3DBackgroundColor =
  object3DBackgroundColorHooks.useSection;
export const useMaybeSectionMediaObject3DBackgroundColor =
  object3DBackgroundColorHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaObject3DBackgroundColor =
  object3DBackgroundColorHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaObject3DBackgroundColor =
  object3DBackgroundColorHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaObject3DBackgroundColor =
  object3DBackgroundColorHooks.usePanel;
/** Effective object3DBackgroundColor for the media settings drawer at the given settings level. */
export const useMediaObject3DBackgroundColorDisplay =
  object3DBackgroundColorHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for object3DBackgroundColor at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaObject3DBackgroundColorResetAtLevel =
  object3DBackgroundColorHooks.useShouldShowReset;

// disableVideoStream hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const disableVideoStreamHooks = mediaSettings.disableVideoStream.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaDisableVideoStream =
  disableVideoStreamHooks.useWorkspace;
export const useMaybeWorkspaceMediaDisableVideoStream =
  disableVideoStreamHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaDisableVideoStream =
  disableVideoStreamHooks.useSection;
export const useMaybeSectionMediaDisableVideoStream =
  disableVideoStreamHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaDisableVideoStream =
  disableVideoStreamHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaDisableVideoStream =
  disableVideoStreamHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaDisableVideoStream = disableVideoStreamHooks.usePanel;
/** Effective disableVideoStream for the media settings drawer at the given settings level. */
export const useMediaDisableVideoStreamDisplay =
  disableVideoStreamHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for disableVideoStream at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaDisableVideoStreamResetAtLevel =
  disableVideoStreamHooks.useShouldShowReset;

// alwaysOriginal hooks are generated by the settings factory; these named exports are
// thin wrappers so existing call sites don't change. See
// ../media/settingFactory/mediaSettingsTable.ts.
const alwaysOriginalHooks = mediaSettings.alwaysOriginal.hooks;

/** Raw workspace-level value from view spec. */
export const useWorkspaceMediaAlwaysOriginal = alwaysOriginalHooks.useWorkspace;
export const useMaybeWorkspaceMediaAlwaysOriginal =
  alwaysOriginalHooks.useMaybeWorkspace;
/** Raw section-level value from view spec (not cascaded from workspace). */
export const useSectionMediaAlwaysOriginal = alwaysOriginalHooks.useSection;
export const useMaybeSectionMediaAlwaysOriginal =
  alwaysOriginalHooks.useMaybeSection;
/** Workspace → section cascaded value. */
export const useCascadingSectionMediaAlwaysOriginal =
  alwaysOriginalHooks.useCascadingSection;
export const useMaybeCascadingSectionMediaAlwaysOriginal =
  alwaysOriginalHooks.useMaybeCascadingSection;
/** Raw panel-level value from the panel's own config (not cascaded). */
export const usePanelMediaAlwaysOriginal = alwaysOriginalHooks.usePanel;
/** Effective alwaysOriginal for the media settings drawer at the given settings level. */
export const useMediaAlwaysOriginalDisplay = alwaysOriginalHooks.useDisplay;
/**
 * Whether to show the reset-to-parent button for alwaysOriginal at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export const useShouldShowMediaAlwaysOriginalResetAtLevel =
  alwaysOriginalHooks.useShouldShowReset;
